import React, { useEffect } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { useGetProblems } from "../../../features/patient-stats/problems/model/use-get-problems";
import { getProblems } from "../../../entities/patient-stats/problems/problems.selectors";
import { UnifiedDataTable } from "../../../layout/unified-data-table/ui/unified-data-table";

import * as Styled from "./problems.styled";

interface IPatientInfoOutletContext {
  setIsStretchContent: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Problems = () => {
  const patientId = useParams().patientID;
  const outletContext = useOutletContext<
    IPatientInfoOutletContext | undefined
  >();
  const setIsStretchContent = outletContext?.setIsStretchContent;
  const contentScrollerRef = React.useRef<HTMLDivElement | null>(null);

  const { getProblemsByPatientId } = useGetProblems();

  useEffect(() => {
    if (!patientId) return;
    getProblemsByPatientId(patientId);
  }, [patientId]);

  useEffect(() => {
    if (!setIsStretchContent) {
      return;
    }

    setIsStretchContent(true);

    return () => {
      setIsStretchContent(false);
    };
  }, [setIsStretchContent]);

  const problems = useSelector(getProblems);

  if (!problems) return null;

  const downloadLinks = problems.sections?.[0]?.tables?.[0]?.ui?.downloadLinks;

  return (
    <Styled.Container>
      <Styled.PageHeader>
        <Styled.Title>Problems</Styled.Title>
        <Styled.Download>
          <div>Download All</div>
          <Styled.DownloadOptions>
            <a href={downloadLinks?.csv} download="problems.csv">
              <svg
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="38" height="38" rx="6" fill="#F8F8F8" />
                <path
                  d="M11 19.9998V11.002C11 9.89651 11.8968 9.00072 13.0022 9.00195L19.9958 9.00973L27 15.9998V19.9998M20 9.49975V13.9998C20 15.1043 20.8954 15.9998 22 15.9998H26.5"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20 23H18C17.4477 23 17 23.4477 17 24V25C17 25.5523 17.4477 26 18 26H19C19.5523 26 20 26.4477 20 27V28C20 28.5523 19.5523 29 19 29H17M22.5 23L24.75 29L27 23M14.5 28C14.5 28.5523 14.0523 29 13.5 29H12C11.4477 29 11 28.5523 11 28V24C11 23.4477 11.4477 23 12 23H13.5C14.0523 23 14.5 23.4477 14.5 24"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
            <a href={downloadLinks?.xls} download="problems.xlsx">
              <svg
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="38" height="38" rx="6" fill="#F8F8F8" />
                <path
                  d="M17.5 23V29H21"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11 23L13 26M13 26L15 29M13 26L15 23M13 26L11 29"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M27.0002 23H23.5V26H27.0002V29H23.5"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11 19.9978L11.0199 10.9956C11.0224 9.89186 11.9184 8.99877 13.0222 9L19.9958 9.00776L27 15.9948L26.9922 19.9978M20 9.49778V13.9978C20 15.1024 20.8954 15.9978 22 15.9978H26.5"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
            <a href={downloadLinks?.pdf} download="problems.pdf">
              <svg
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="38" height="38" rx="6" fill="#F8F8F8" />
                <path
                  d="M11 19.9978L11.0199 10.9956C11.0224 9.89186 11.9185 8.99877 13.0222 9L19.9958 9.00776L27 15.9948L26.9922 19.9978M20 9.49778V13.9978C20 15.1024 20.8954 15.9978 22 15.9978H26.5"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M26.5 23.75C26.9142 23.75 27.25 23.4142 27.25 23C27.25 22.5858 26.9142 22.25 26.5 22.25V23.75ZM23 23V22.25C22.5858 22.25 22.25 22.5858 22.25 23H23ZM22.25 29C22.25 29.4142 22.5858 29.75 23 29.75C23.4142 29.75 23.75 29.4142 23.75 29H22.25ZM26 26.75C26.4142 26.75 26.75 26.4142 26.75 26C26.75 25.5858 26.4142 25.25 26 25.25V26.75ZM11 23V22.25C10.5858 22.25 10.25 22.5858 10.25 23H11ZM10.25 29C10.25 29.4142 10.5858 29.75 11 29.75C11.4142 29.75 11.75 29.4142 11.75 29H10.25ZM17 23V22.25C16.5858 22.25 16.25 22.5858 16.25 23H17ZM17 29H16.25C16.25 29.4142 16.5858 29.75 17 29.75V29ZM26.5 22.25H23V23.75H26.5V22.25ZM22.25 23V26H23.75V23H22.25ZM22.25 26V29H23.75V26H22.25ZM23 26.75H26V25.25H23V26.75ZM11 23.75H12.75V22.25H11V23.75ZM11.75 29V26.5H10.25V29H11.75ZM11.75 26.5V23H10.25V26.5H11.75ZM12.75 25.75H11V27.25H12.75V25.75ZM13.75 24.75C13.75 25.3023 13.3023 25.75 12.75 25.75V27.25C14.1307 27.25 15.25 26.1307 15.25 24.75H13.75ZM12.75 23.75C13.3023 23.75 13.75 24.1977 13.75 24.75H15.25C15.25 23.3693 14.1307 22.25 12.75 22.25V23.75ZM17 23.75H18.5V22.25H17V23.75ZM19.75 25V27H21.25V25H19.75ZM18.5 28.25H17V29.75H18.5V28.25ZM17.75 29V23H16.25V29H17.75ZM19.75 27C19.75 27.6904 19.1904 28.25 18.5 28.25V29.75C20.0188 29.75 21.25 28.5188 21.25 27H19.75ZM18.5 23.75C19.1904 23.75 19.75 24.3096 19.75 25H21.25C21.25 23.4812 20.0188 22.25 18.5 22.25V23.75Z"
                  fill="#181818"
                />
              </svg>
            </a>
          </Styled.DownloadOptions>
        </Styled.Download>
      </Styled.PageHeader>
      <UnifiedDataTable
        record={problems}
        useSharedSectionNav
        scrollContainerRef={contentScrollerRef}
      />
    </Styled.Container>
  );
};
